import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Button } from '@mui/material';
import { SAGE, CREAM, WARM } from '../theme';
import { API_URL } from '../config';
import { mockAuthHeaders } from '../utils/mockAuth';

// ============================================================================
// CAREER TIMELINE — Fixed-lane layout
//
// Central trunk with 3 lanes on each side (L3 L2 L1 | TRUNK | R1 R2 R3).
// Items placed earliest→latest. Each new item goes to the side with the
// most available lanes, in the nearest free lane.
// Jobs/education = branches. Projects = sprouts (inherit parent side).
// ============================================================================

const F = "'Outfit', sans-serif";

const TYPE_COLORS = {
  job:       { main: SAGE[500],   bg: SAGE[100],  border: SAGE[400] },
  education: { main: '#6a7fc4',   bg: '#e8edf5',  border: '#8899cc' },
  project:   { main: WARM.accent, bg: '#f0e6d8',  border: '#c4a070' },
};

const IC = {
  briefcase: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>,
  cap: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5"/></svg>,
  folder: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>,
  chevDown: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>,
  chevUp: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="18 15 12 9 6 15"/></svg>,
  plus: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  award: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></svg>,
  star: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 3l1.5 4.5H18l-3.5 2.8 1.3 4.2L12 12l-3.8 2.5 1.3-4.2L6 7.5h4.5z"/></svg>,
};
const ICON_MAP = { job: IC.briefcase, education: IC.cap, project: IC.folder };

// ============================================================================
// CONSTANTS
// ============================================================================
const PX_PER_MONTH = 12;
const NUM_LANES = 3;                 // lanes per side
const TRUNK_GAP = 30;                // px from center to first lane edge
const LANE_GAP = 6;                  // px between lanes
const CARD_MIN_PX = 60;              // minimum card height even for short durations
const CARD_PADDING_TOP = 30;         // space above first item for "Now" indicator

// ============================================================================
// TIME HELPERS
// ============================================================================
function normalizeDate(dateStr) {
  if (!dateStr || dateStr === 'Present') return dateStr;
  if (/^\d{4}-\d{2}/.test(dateStr)) return dateStr;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function formatDate(dateStr) {
  if (!dateStr || dateStr === 'Present') return 'Present';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function buildTimeScale(items) {
  const now = new Date();
  let earliest = now;
  for (const item of items) {
    const s = item.start ? new Date(item.start) : null;
    if (s && s < earliest) earliest = s;
  }
  const endDate = new Date(now.getFullYear(), now.getMonth() + 6, 1);
  const startDate = new Date(earliest.getFullYear(), earliest.getMonth() - 6, 1);
  const totalMonths = (endDate.getFullYear() - startDate.getFullYear()) * 12
    + (endDate.getMonth() - startDate.getMonth());

  const dateToY = (dateStr) => {
    if (!dateStr || dateStr === 'Present') return CARD_PADDING_TOP;
    const d = new Date(dateStr);
    const months = (endDate.getFullYear() - d.getFullYear()) * 12
      + (endDate.getMonth() - d.getMonth());
    return CARD_PADDING_TOP + Math.max(0, months * PX_PER_MONTH);
  };

  const yToDate = (y) => {
    const months = Math.round((y - CARD_PADDING_TOP) / PX_PER_MONTH);
    return new Date(endDate.getFullYear(), endDate.getMonth() - months, 1);
  };

  const years = [];
  for (let y = endDate.getFullYear(); y >= startDate.getFullYear(); y--) {
    const yPos = dateToY(`${y}-01-01`);
    if (yPos > CARD_PADDING_TOP && yPos < CARD_PADDING_TOP + totalMonths * PX_PER_MONTH) {
      years.push({ year: y, y: yPos });
    }
  }

  return { dateToY, yToDate, totalHeight: CARD_PADDING_TOP + totalMonths * PX_PER_MONTH, years };
}

// ============================================================================
// DATA — flatten profile into timeline items (branches + sprouts)
// ============================================================================
function profileToItems(profile) {
  const items = [];

  (profile.experience || []).forEach((exp, idx) => {
    const start = exp.start_date ? normalizeDate(exp.start_date) : null;
    const end = exp.end_date ? normalizeDate(exp.end_date) : 'Present';
    const branchId = `exp-${idx}`;

    items.push({
      id: branchId,
      type: 'job',
      title: exp.title || 'Untitled Role',
      subtitle: exp.company || '',
      start, end,
      bullets: (exp.accomplishments?.length || 0),
      _parentId: null,
    });

    (exp.projects || []).forEach((proj, pidx) => {
      items.push({
        id: proj.id || `${branchId}-p${pidx}`,
        type: 'project',
        title: proj.title || 'Untitled Project',
        subtitle: exp.company || '',
        start, end, // projects inherit parent time range for positioning
        bullets: proj.bullets?.length || 0,
        status: proj.status || 'draft',
        conversationCount: proj.conversation_ids?.length || 0,
        _parentId: branchId,
        _project: proj,
      });
    });
  });

  (profile.education || []).forEach((edu, idx) => {
    const start = edu.start_date ? normalizeDate(edu.start_date) : null;
    const end = edu.end_date ? normalizeDate(edu.end_date) : 'Present';
    const branchId = `edu-${idx}`;

    items.push({
      id: branchId,
      type: 'education',
      title: edu.degree || edu.field_of_study || 'Education',
      subtitle: edu.institution || '',
      start, end,
      bullets: 0,
      _parentId: null,
    });

    (edu.projects || []).forEach((proj, pidx) => {
      items.push({
        id: proj.id || `${branchId}-p${pidx}`,
        type: 'project',
        title: proj.title || 'Untitled Project',
        subtitle: edu.institution || '',
        start, end,
        bullets: proj.bullets?.length || 0,
        status: proj.status || 'draft',
        conversationCount: proj.conversation_ids?.length || 0,
        _parentId: branchId,
        _project: proj,
      });
    });
  });

  return items;
}

// ============================================================================
// LANE ASSIGNMENT — 3 lanes per side, earliest-first, balance sides
// ============================================================================
// Returns: { [itemId]: { side: 'left'|'right', lane: 1|2|3 } }
function assignLanes(items, dateToY) {
  // Sort by start date (earliest first). Use end date as tiebreaker.
  const sorted = [...items].sort((a, b) => {
    const aStart = a.start ? new Date(a.start).getTime() : Date.now();
    const bStart = b.start ? new Date(b.start).getTime() : Date.now();
    if (aStart !== bStart) return aStart - bStart;
    const aEnd = a.end === 'Present' ? Date.now() : new Date(a.end).getTime();
    const bEnd = b.end === 'Present' ? Date.now() : new Date(b.end).getTime();
    return aEnd - bEnd;
  });

  // Each lane tracks occupied intervals as [topY, bottomY] pairs
  const lanes = {
    left:  [[], [], []], // lanes 1, 2, 3
    right: [[], [], []],
  };

  const assignments = {};

  // Check if an item fits in a lane (no temporal overlap)
  const fits = (laneIntervals, topY, bottomY) => {
    for (const [oTop, oBot] of laneIntervals) {
      if (topY < oBot && bottomY > oTop) return false;
    }
    return true;
  };

  // Count available lanes on a side for a given time range
  const countAvailable = (side, topY, bottomY) => {
    let count = 0;
    for (let l = 0; l < NUM_LANES; l++) {
      if (fits(lanes[side][l], topY, bottomY)) count++;
    }
    return count;
  };

  // Find nearest available lane (0-indexed) on a side
  const findNearest = (side, topY, bottomY) => {
    for (let l = 0; l < NUM_LANES; l++) {
      if (fits(lanes[side][l], topY, bottomY)) return l;
    }
    return -1;
  };

  // Global prior balance: count of items placed on each side so far
  let globalLeft = 0;
  let globalRight = 0;

  for (const item of sorted) {
    const topY = dateToY(item.end || 'Present');
    const bottomY = item.start ? dateToY(item.start) : topY;
    const cardBottom = Math.max(bottomY, topY + CARD_MIN_PX);

    // If this is a project, try to go on same side as parent
    let preferredSide = null;
    if (item._parentId && assignments[item._parentId]) {
      preferredSide = assignments[item._parentId].side;
    }

    const leftAvail = countAvailable('left', topY, cardBottom);
    const rightAvail = countAvailable('right', topY, cardBottom);

    let side, laneIdx;

    if (preferredSide) {
      // Try preferred side first
      const prefIdx = findNearest(preferredSide, topY, cardBottom);
      if (prefIdx >= 0) {
        side = preferredSide;
        laneIdx = prefIdx;
      } else {
        // Fall back to other side
        const otherSide = preferredSide === 'left' ? 'right' : 'left';
        const otherIdx = findNearest(otherSide, topY, cardBottom);
        if (otherIdx >= 0) {
          side = otherSide;
          laneIdx = otherIdx;
        } else {
          side = preferredSide;
          laneIdx = 0;
        }
      }
    } else {
      // 1. Local balance: which side has more available lanes at this time range
      // 2. Global prior balance: which side has fewer items overall (for alternation)
      if (leftAvail > rightAvail && leftAvail > 0) {
        side = 'left';
      } else if (rightAvail > leftAvail && rightAvail > 0) {
        side = 'right';
      } else if (leftAvail > 0 && rightAvail > 0) {
        // Tied locally — use global prior balance to alternate
        side = globalLeft <= globalRight ? 'left' : 'right';
      } else if (leftAvail > 0) {
        side = 'left';
      } else if (rightAvail > 0) {
        side = 'right';
      } else {
        // Both sides full — use global balance
        side = globalLeft <= globalRight ? 'left' : 'right';
      }
      laneIdx = findNearest(side, topY, cardBottom);
      if (laneIdx < 0) laneIdx = 0;
    }

    lanes[side][laneIdx].push([topY, cardBottom]);
    assignments[item.id] = { side, lane: laneIdx + 1 }; // 1-indexed lanes

    // Update global balance counters
    if (side === 'left') globalLeft++;
    else globalRight++;
  }

  return assignments;
}

// ============================================================================
// LANE GEOMETRY — compute pixel positions for each lane
// ============================================================================
// Returns: getLaneX(side, lane) → { left, width } as CSS calc strings
function useLaneGeometry(containerWidth) {
  // Each side gets 50% minus the trunk gap. Divide into NUM_LANES columns.
  // lane is 1-indexed: 1 = nearest trunk, 3 = outermost
  const getLaneStyle = (side, lane) => {
    // Available width per side: 50% - TRUNK_GAP
    // Each lane width: (50% - TRUNK_GAP - (NUM_LANES-1)*LANE_GAP) / NUM_LANES
    const laneWidth = `calc((50% - ${TRUNK_GAP + (NUM_LANES - 1) * LANE_GAP}px) / ${NUM_LANES})`;
    const offset = `calc(${TRUNK_GAP}px + ${(lane - 1)} * (((50% - ${TRUNK_GAP}px) - ${(NUM_LANES - 1) * LANE_GAP}px) / ${NUM_LANES} + ${LANE_GAP}px))`;

    if (side === 'right') {
      return {
        left: `calc(50% + ${offset})`,
        width: laneWidth,
      };
    } else {
      // Left side: mirror. Lane 1 is closest to center (right-most on left side)
      return {
        right: `calc(50% + ${offset})`,
        width: laneWidth,
      };
    }
  };

  return getLaneStyle;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function ProfileTimelinePage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [showAddMenu, setShowAddMenu] = useState(null);
  const timelineRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/profile-manager`, {
          headers: { ...mockAuthHeaders() },
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to load profile');
        setProfile(await res.json());
      } catch (err) {
        console.error('[TIMELINE] Error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const items = useMemo(() => profile ? profileToItems(profile) : [], [profile]);
  const timeScale = useMemo(() => buildTimeScale(items), [items]);
  const { dateToY, yToDate, totalHeight, years } = timeScale;
  const assignments = useMemo(() => assignLanes(items, dateToY), [items, dateToY]);
  const getLaneStyle = useLaneGeometry();

  if (loading || !profile) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: SAGE[500] }} />
      </Box>
    );
  }

  const handleTimelineClick = (e) => {
    if (e.target.closest('[data-item]') || e.target.closest('[data-menu]')) return;
    const rect = timelineRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top + timelineRef.current.scrollTop;
    setShowAddMenu({ y });
    setExpandedItem(null);
  };

  const branchCount = items.filter(i => !i._parentId).length;
  const projectCount = items.filter(i => i._parentId).length;
  const bulletCount = items.reduce((s, i) => s + (i.bullets || 0), 0);

  return (
    <div style={{ fontFamily: "'Lora', Georgia, serif", minHeight: '100vh' }}>
      <style>{`
        @keyframes pulse-ring{0%{transform:scale(1);opacity:0.5}100%{transform:scale(1.8);opacity:0}}
        .pulse-ring{animation:pulse-ring 1.5s ease-out infinite}
      `}</style>

      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 40,
        background: `${CREAM[50]}e6`, backdropFilter: 'blur(12px)',
        borderBottom: `2px solid ${SAGE[200]}`,
      }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: SAGE[800], fontFamily: F }}>
              Career Timeline
            </h1>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: SAGE[400], fontFamily: F }}>
              Grow your career — click a branch to expand · click the trunk to plant
            </p>
          </div>
          <Button
            onClick={() => navigate('/profile')}
            sx={{ textTransform: 'none', color: SAGE[600], borderColor: SAGE[200], fontFamily: F, fontSize: 12 }}
            variant="outlined" size="small"
          >
            Table View
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '16px 32px 8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {[
            { type: 'job', label: 'Job' },
            { type: 'education', label: 'Education' },
            { type: 'project', label: 'Project' },
          ].map(({ type, label }) => (
            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: TYPE_COLORS[type].main }} />
              <span style={{ fontSize: 10, fontWeight: 500, color: SAGE[500], fontFamily: F }}>{label}</span>
            </div>
          ))}
          <span style={{ fontSize: 10, marginLeft: 'auto', color: SAGE[300], fontFamily: F }}>
            {branchCount} branches · {projectCount} projects · {bulletCount} bullets
          </span>
        </div>
      </div>

      {/* Timeline */}
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 16px 64px' }}>
        <div
          ref={timelineRef}
          style={{ position: 'relative', height: totalHeight + 100, cursor: 'crosshair' }}
          onClick={handleTimelineClick}
        >
          {/* Central trunk line */}
          <div style={{
            position: 'absolute', left: '50%', top: 0, bottom: 0, width: 3,
            transform: 'translateX(-50%)',
            background: `linear-gradient(to bottom, ${SAGE[400]}, ${SAGE[200]} 8%, ${SAGE[200]} 92%, ${SAGE[100]})`,
            borderRadius: 2,
          }} />

          {/* 6 trunk lane lines (3 per side) — thin vertical guides */}
          {[1, 2, 3].map(lane => ['left', 'right'].map(side => {
            const laneStyle = getLaneStyle(side, lane);
            return (
              <div key={`trunk-${side}-${lane}`} style={{
                position: 'absolute', top: 0, bottom: 0,
                ...laneStyle,
                // Override width to a thin line centered in the lane
                width: 1,
                // Center in the lane: offset by half lane width
                ...(side === 'right'
                  ? { marginLeft: `calc((50% - ${TRUNK_GAP + (NUM_LANES - 1) * LANE_GAP}px) / ${NUM_LANES} / 2)` }
                  : { marginRight: `calc((50% - ${TRUNK_GAP + (NUM_LANES - 1) * LANE_GAP}px) / ${NUM_LANES} / 2)` }
                ),
                background: `${SAGE[200]}60`,
                zIndex: 0,
              }} />
            );
          }))}

          {/* Trunk lane duration bars — colored bar segments on each item's lane */}
          {items.map(item => {
            const assignment = assignments[item.id];
            if (!assignment) return null;
            const { side, lane } = assignment;
            const c = TYPE_COLORS[item.type] || TYPE_COLORS.job;
            const topY = dateToY(item.end || 'Present');
            const bottomY = item.start ? dateToY(item.start) : topY;
            const barHeight = Math.max(bottomY - topY, 4);
            const laneStyle = getLaneStyle(side, lane);
            return (
              <div key={`bar-${item.id}`} style={{
                position: 'absolute',
                top: topY,
                ...laneStyle,
                // Narrow bar centered in the lane
                width: 4,
                ...(side === 'right'
                  ? { marginLeft: `calc((50% - ${TRUNK_GAP + (NUM_LANES - 1) * LANE_GAP}px) / ${NUM_LANES} / 2 - 2px)` }
                  : { marginRight: `calc((50% - ${TRUNK_GAP + (NUM_LANES - 1) * LANE_GAP}px) / ${NUM_LANES} / 2 - 2px)` }
                ),
                height: barHeight,
                borderRadius: 2,
                background: `${c.main}50`,
                zIndex: 1,
              }} />
            );
          })}

          {/* "Now" indicator */}
          <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: CARD_PADDING_TOP, zIndex: 10 }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div className="pulse-ring" style={{ width: 14, height: 14, borderRadius: '50%', background: SAGE[400], position: 'absolute' }} />
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: SAGE[500], border: `3px solid ${CREAM[50]}`, position: 'relative', zIndex: 2 }} />
            </div>
            <div style={{
              position: 'absolute', left: '50%', transform: 'translateX(-50%)', marginTop: 4,
              padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap',
              background: SAGE[500], color: CREAM[50], fontFamily: F,
            }}>Now</div>
          </div>

          {/* Year markers */}
          {years.map(({ year, y }) => (
            <div key={year} style={{
              position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: y,
            }}>
              <div style={{ width: 16, height: 1, background: SAGE[300], marginLeft: -8 }} />
              {/* Render year on alternating sides to avoid overlap */}
              <span style={{
                position: 'absolute', fontSize: 10, fontWeight: 600,
                color: SAGE[300], fontFamily: F,
                ...(year % 2 === 0
                  ? { left: 16, transform: 'translateY(-50%)' }
                  : { right: 16, transform: 'translateY(-50%)' }),
              }}>{year}</span>
            </div>
          ))}

          {/* Item cards */}
          {items.map((item) => {
            const assignment = assignments[item.id];
            if (!assignment) return null;

            const { side, lane } = assignment;
            const c = TYPE_COLORS[item.type] || TYPE_COLORS.job;
            const icon = ICON_MAP[item.type] || IC.briefcase;
            const isExpanded = expandedItem === item.id;
            const isHovered = hoveredItem === item.id;
            const isProject = item.type === 'project';

            const topY = dateToY(item.end || 'Present');
            const bottomY = item.start ? dateToY(item.start) : topY;
            const cardHeight = Math.max(bottomY - topY, CARD_MIN_PX);

            // Get lane CSS positioning
            const laneStyle = getLaneStyle(side, lane);

            // Connector: horizontal line from trunk to card
            const connectorY = topY + Math.min(cardHeight / 2, 30);

            return (
              <div key={item.id} data-item="true">
                {/* Trunk notch — small mark where this item connects */}
                <div style={{
                  position: 'absolute',
                  left: side === 'left' ? `calc(50% - 8px)` : '50%',
                  top: connectorY - 1,
                  width: 8, height: 3, borderRadius: 1,
                  background: isHovered || isExpanded ? c.main : SAGE[300],
                  zIndex: 2, transition: 'background 0.2s',
                }} />

                {/* Horizontal connector */}
                <div style={{
                  position: 'absolute',
                  top: connectorY,
                  // Connector spans from trunk to the card's near edge
                  left: side === 'right' ? `calc(50% + 2px)` : undefined,
                  right: side === 'left' ? `calc(50% + 2px)` : undefined,
                  width: `calc(${TRUNK_GAP}px + ${(lane - 1)} * (((50% - ${TRUNK_GAP}px) - ${(NUM_LANES - 1) * LANE_GAP}px) / ${NUM_LANES} + ${LANE_GAP}px))`,
                  height: 1,
                  background: isHovered || isExpanded ? c.main : SAGE[200],
                  zIndex: 1, transition: 'background 0.2s',
                }} />

                {/* Card */}
                <div
                  style={{
                    position: 'absolute',
                    top: topY,
                    ...laneStyle,
                    minHeight: cardHeight,
                    zIndex: isExpanded ? 25 : isHovered ? 12 : 3,
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s, border-color 0.2s',
                  }}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedItem(isExpanded ? null : item.id);
                    setShowAddMenu(null);
                  }}
                >
                  <div style={{
                    height: '100%',
                    borderRadius: 10,
                    background: isExpanded || isHovered ? CREAM[50] : `${c.bg}cc`,
                    border: `1.5px solid ${isExpanded ? c.main : isHovered ? SAGE[300] : SAGE[200]}`,
                    borderLeft: `4px solid ${c.main}`,
                    boxShadow: isExpanded ? `0 6px 24px ${c.main}18` : 'none',
                    backdropFilter: 'blur(8px)',
                    overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                  }}>
                    {/* Card content */}
                    <div style={{ padding: '10px 12px', flex: 1, minHeight: 0 }}>
                      {/* Header */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: c.main, flexShrink: 0 }}>{icon}</span>
                        <span style={{
                          fontSize: 12, fontWeight: 600, color: SAGE[800], fontFamily: F,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                        }}>{item.title}</span>
                        <span style={{ color: SAGE[300], flexShrink: 0 }}>
                          {isExpanded ? IC.chevUp : IC.chevDown}
                        </span>
                      </div>

                      {/* Subtitle */}
                      <div style={{
                        fontSize: 10, color: SAGE[500], fontFamily: F, marginTop: 2,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{item.subtitle}</div>

                      {/* Date range */}
                      <div style={{ fontSize: 9, color: SAGE[400], fontFamily: F, marginTop: 2 }}>
                        {formatDate(item.start)} → {formatDate(item.end)}
                      </div>

                      {/* Tags */}
                      <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                        {isProject && item.status && (
                          <span style={{
                            fontSize: 8, padding: '1px 5px', borderRadius: 3, fontWeight: 500,
                            background: item.status === 'polished' ? SAGE[100] : CREAM[200],
                            color: item.status === 'polished' ? SAGE[700] : SAGE[500], fontFamily: F,
                          }}>{item.status}</span>
                        )}
                        {item.bullets > 0 && (
                          <span style={{
                            fontSize: 8, padding: '1px 5px', borderRadius: 3, fontWeight: 500,
                            background: SAGE[100], color: SAGE[600], fontFamily: F,
                          }}>{item.bullets} bullet{item.bullets !== 1 ? 's' : ''}</span>
                        )}
                        {isProject && item.conversationCount > 0 && (
                          <span style={{
                            fontSize: 8, padding: '1px 5px', borderRadius: 3, fontWeight: 500,
                            background: '#e8edf5', color: '#6a7fc4', fontFamily: F,
                          }}>{item.conversationCount} conv</span>
                        )}
                      </div>
                    </div>

                    {/* Expanded section */}
                    {isExpanded && (
                      <div style={{
                        padding: '8px 12px 12px', borderTop: `1px solid ${SAGE[200]}`,
                        background: `${CREAM[50]}`,
                      }}>
                        {/* Bullet previews (projects) */}
                        {isProject && item._project?.bullets?.length > 0 && (
                          <div style={{ marginBottom: 8 }}>
                            {item._project.bullets.slice(0, 3).map((bullet, i) => (
                              <div key={i} style={{
                                fontSize: 10, color: SAGE[600], fontFamily: "'Lora', serif",
                                padding: '3px 0', lineHeight: 1.4,
                                borderBottom: i < Math.min(item._project.bullets.length, 3) - 1
                                  ? `1px solid ${SAGE[100]}` : 'none',
                              }}>
                                • {typeof bullet === 'string' ? bullet : bullet.text || bullet.content || ''}
                              </div>
                            ))}
                            {item._project.bullets.length > 3 && (
                              <div style={{ fontSize: 9, color: SAGE[400], fontFamily: F, marginTop: 2 }}>
                                +{item._project.bullets.length - 3} more
                              </div>
                            )}
                          </div>
                        )}

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 6 }}>
                          {isProject ? (
                            <>
                              <button
                                style={{
                                  flex: 1, padding: '5px 8px', borderRadius: 6, fontSize: 10,
                                  fontWeight: 500, fontFamily: F, border: 'none', cursor: 'pointer',
                                  background: c.main, color: '#fff',
                                }}
                                onClick={(e) => { e.stopPropagation(); navigate(`/projects/${item.id}`); }}
                              >Open Project</button>
                              <button
                                style={{
                                  flex: 1, padding: '5px 8px', borderRadius: 6, fontSize: 10,
                                  fontWeight: 500, fontFamily: F, cursor: 'pointer',
                                  border: `1px solid ${SAGE[200]}`, color: SAGE[600], background: 'transparent',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/profile/enhance?projectId=${item.id}&projectTitle=${encodeURIComponent(item.title)}`);
                                }}
                              >Grow with Conversation</button>
                            </>
                          ) : (
                            <>
                              <button
                                style={{
                                  flex: 1, padding: '5px 8px', borderRadius: 6, fontSize: 10,
                                  fontWeight: 500, fontFamily: F, border: 'none', cursor: 'pointer',
                                  background: c.main, color: '#fff',
                                }}
                                onClick={(e) => { e.stopPropagation(); navigate('/profile'); }}
                              >Edit in Profile</button>
                              <button
                                style={{
                                  flex: 1, padding: '5px 8px', borderRadius: 6, fontSize: 10,
                                  fontWeight: 500, fontFamily: F, cursor: 'pointer',
                                  border: `1px solid ${SAGE[200]}`, color: SAGE[600], background: 'transparent',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/profile/enhance?projectTitle=${encodeURIComponent(item.title + ' at ' + item.subtitle)}`);
                                }}
                              >Start Conversation</button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Add Menu */}
          {showAddMenu && (
            <div data-menu="true" style={{
              position: 'absolute', zIndex: 30,
              top: showAddMenu.y - 10, left: '50%', transform: 'translateX(-50%)',
            }}>
              <div style={{
                borderRadius: 12, padding: 8,
                background: CREAM[50], border: `2px solid ${SAGE[300]}`,
                boxShadow: `0 12px 40px ${SAGE[800]}20`, minWidth: 180,
              }}>
                <div style={{ padding: '6px 8px', marginBottom: 4 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: SAGE[400], fontFamily: F }}>
                    Plant at {yToDate(showAddMenu.y).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </span>
                </div>
                {[
                  { type: 'job', label: 'Job / Role', icon: IC.briefcase, desc: 'A new branch' },
                  { type: 'education', label: 'Education', icon: IC.cap, desc: 'Degree or program' },
                  { type: 'project', label: 'Side Project', icon: IC.folder, desc: 'Independent work' },
                  { type: 'certification', label: 'Certification', icon: IC.award, desc: 'License or credential' },
                  { type: 'event', label: 'Milestone', icon: IC.star, desc: 'Conference, award, etc.' },
                ].map((opt) => {
                  const oc = TYPE_COLORS[opt.type] || TYPE_COLORS.job;
                  return (
                    <button
                      key={opt.type}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/profile');
                        setShowAddMenu(null);
                      }}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 12px', borderRadius: 8, textAlign: 'left',
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = oc.bg; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <div style={{
                        width: 28, height: 28, borderRadius: 8,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: oc.bg, color: oc.main,
                      }}>{opt.icon}</div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: SAGE[800], fontFamily: F }}>{opt.label}</div>
                        <div style={{ fontSize: 10, color: SAGE[400], fontFamily: F }}>{opt.desc}</div>
                      </div>
                    </button>
                  );
                })}
                <div style={{ marginTop: 4, paddingTop: 4, borderTop: `1px solid ${SAGE[200]}` }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowAddMenu(null); }}
                    style={{
                      width: '100%', textAlign: 'center', padding: '6px 0',
                      fontSize: 10, fontWeight: 500, color: SAGE[400], fontFamily: F,
                      background: 'none', border: 'none', cursor: 'pointer',
                    }}
                  >Cancel</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
